import {
  ChevronLeft,
  CircleUserRound,
  MoreVertical,
  Pin,
  PinOff,
  Search,
  UsersRound,
  Volume2,
  VolumeX,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DisplayNameSkeleton } from '@/components';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdownMenu/dropdown-menu';
import { constants } from '@/lib/constants';
import { cn, getPresenceColor } from '@/lib/utils';
import { NoChatSelected } from './NoChatSelected';
import { Profile } from './Profile';
import SearchChat from './SearchChat';
import { SelectedChat } from './SelectedChat';
import { useERSContext } from '@/context';

export const ChatWindow = ({
  setSelectedChat,
  selectedChat,
  chatMembers,
  onTogglePin,
  isChatPinned,
  isChatMuted,
  onToggleMute,
}) => {
  const { t } = useTranslation();
  const [view, setView] = useState(constants.CHAT_PANEL.CHAT);
  const [messageToScrollTo, setMessageToScrollTo] = useState(null);
  const { users } = useERSContext();
  useEffect(() => {
    if (selectedChat) {
      setView(constants.CHAT_PANEL.CHAT);
    } else {
      setView(constants.CHAT_PANEL.NONE);
    }
  }, [selectedChat]);

  // Split display name into serial ID and incident type
  const splitDisplayName = displayName => {
    if (!displayName) return { serialId: '', incidentType: '' };

    const parts = displayName.split(' - ');
    if (parts.length === 2) {
      return { serialId: parts[0], incidentType: parts[1] };
    }
    return { serialId: '', incidentType: displayName };
  };

  // Get translated incident type from translation files
  const getTranslatedIncidentType = incidentType => {
    if (!incidentType) return '';

    // Get all incident types from translation
    const incidentTypes = t('INCIDENT_TYPES', { returnObjects: true });
    if (!Array.isArray(incidentTypes)) return incidentType;

    // Find the matching incident type and return its translated label
    const matchedType = incidentTypes.find(type => type.value === incidentType);
    return matchedType?.label || incidentType;
  };

  // Get translated display name using translation files
  const getTranslatedDisplayName = () => {
    if (!selectedChat?.displayName) return '';

    const { serialId, incidentType } = splitDisplayName(selectedChat.displayName);

    if (!incidentType) return selectedChat.displayName;

    const translatedIncidentType = getTranslatedIncidentType(incidentType);

    return serialId ? `${serialId} - ${translatedIncidentType}` : translatedIncidentType;
  };

  const handleSearchResultClick = useCallback(message => {
    setMessageToScrollTo(message.id);
    setView(constants.CHAT_PANEL.CHAT);
  }, []);

  const handleMessageScrolled = useCallback(() => {
    setMessageToScrollTo(null);
  }, []);

  const renderView = () => {
    switch (view) {
      case constants.CHAT_PANEL.CHAT:
        return (
          <SelectedChat
            selectedChat={selectedChat}
            chatMembers={chatMembers}
            onTogglePin={onTogglePin}
            isChatPinned={isChatPinned}
            messageToScrollTo={messageToScrollTo}
            onMessageScrolled={handleMessageScrolled}
          />
        );
      case constants.CHAT_PANEL.PROFILE:
        return (
          <Profile
            selectedChat={selectedChat}
            chatMembers={chatMembers}
            onBack={() => setView(constants.CHAT_PANEL.CHAT)}
          />
        );
      case constants.CHAT_PANEL.SEARCH:
        return (
          <SearchChat
            selectedChat={selectedChat}
            onBack={() => setView(constants.CHAT_PANEL.CHAT)}
            onSearchResultClick={handleSearchResultClick}
          />
        );
      default:
        return <NoChatSelected />;
    }
  };

  const handleUsersPresenece = id => {
    const usersObj = users?.filter(user => user?.id == id);
    return getPresenceColor(usersObj[0]?.presence);
  };

  const translatedDisplayName = getTranslatedDisplayName();

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* Header */}
      {view !== constants.CHAT_PANEL.NONE && selectedChat && (
        <div className="p-3 h-16 border-b flex items-center justify-between bg-transparent shrink-0">
          <div className="flex justify-between items-center gap-5">
            <ChevronLeft
              className="cursor-pointer"
              size={20}
              color="var(--color-icon)"
              onClick={() => {
                if (view === constants.CHAT_PANEL.PROFILE || view === constants.CHAT_PANEL.SEARCH) {
                  setView(constants.CHAT_PANEL.CHAT);
                } else {
                  setSelectedChat(null);
                }
              }}
            />
            <div className="flex items-center gap-2">
              {selectedChat.type !== constants.CHAT_PANEL.GROUP ? (
                <div className="relative w-8 h-8 flex justify-center items-center">
                  <CircleUserRound size={24} absoluteStrokeWidth className="text-icon" />
                  <span
                    className={cn(
                      'absolute bottom-1 right-1 block h-2 w-2 rounded-full border-2 border-bg',
                      handleUsersPresenece(selectedChat?.id)
                    )}
                  />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full flex justify-center items-center bg-elevated relative">
                  <UsersRound size={20} absoluteStrokeWidth className="text-icon" />
                </div>
              )}

              <div className="flex flex-col items-start">
                <h2 className="font-semibold text">
                  {view === constants.CHAT_PANEL.CHAT
                    ? translatedDisplayName
                    : view === constants.CHAT_PANEL.SEARCH
                      ? `${t('CHAT_WINDOW.SEARCH_IN')} ${translatedDisplayName}`
                      : t('CHAT_WINDOW.YOUR_PROFILE')}
                </h2>
                {view === constants.CHAT_PANEL.CHAT && (
                  <p className="text-sm text min-w-10 max-w-52 truncate">
                    {(() => {
                      if (selectedChat?.type === 'group' && selectedChat?.members) {
                        const members = Array.from(selectedChat.members.values());
                        const names = members.map(m => m.nick || t('PROFILE_PANEL.MEMBER_FALLBACK_NAME'));
                        return names.length > 3 ? `${names.slice(0, 3).join(', ')} ...` : names.join(', ');
                      }
                      return users.find(u => u.id === selectedChat?.id)?.username || selectedChat?.id;
                    })()}
                  </p>
                )}
              </div>
            </div>
          </div>

          {view === constants.CHAT_PANEL.CHAT && (
            <div className="flex gap-5 items-center">
              {/* Search button */}
              <Button variant="ghost" size="ghostIcon" onClick={() => setView(constants.CHAT_PANEL.SEARCH)}>
                <Search size={16} />
              </Button>

              {/* Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex h-10 w-10 items-center justify-center rounded-sm hover:bg-strong">
                  <MoreVertical size={18} color="var(--color-icon)" />
                </DropdownMenuTrigger>
                <DropdownMenuContent sideOffset={5} align="end" portalled={false}>
                  <DropdownMenuItem
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={() => setView(constants.CHAT_PANEL.PROFILE)}
                  >
                    <CircleUserRound size={15} color="var(--color-icon)" />
                    <span className="text-sm">{t('CHAT_WINDOW.VIEW_PROFILE')}</span>
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={() => onTogglePin(selectedChat.id)}
                  >
                    {isChatPinned(selectedChat.id) ? (
                      <PinOff size={15} color="var(--color-icon)" />
                    ) : (
                      <Pin size={15} color="var(--color-icon)" />
                    )}
                    <span className="text-sm">
                      {isChatPinned(selectedChat.id) ? t('CHAT_WINDOW.UNPIN_CHAT') : t('CHAT_WINDOW.PIN_CHAT')}
                    </span>
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={() => onToggleMute(selectedChat.id)}
                  >
                    {isChatMuted(selectedChat.id) ? (
                      <Volume2 size={15} color="var(--color-icon)" />
                    ) : (
                      <VolumeX size={15} color="var(--color-icon)" />
                    )}
                    <span className="text-sm">
                      {isChatMuted(selectedChat.id) ? t('CHAT_WINDOW.UNMUTE_CHAT') : t('CHAT_WINDOW.MUTE_CHAT')}
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      )}

      {/* Main content (SelectedChat, Profile, etc.) */}
      <div className="flex-1 overflow-hidden">{renderView()}</div>
    </div>
  );
};
